import { Action } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import authStorage from "setup/redux/authStorage";
import { ClientPortalUserModel, ImpersonationTokenModel } from "services/api/cplauthapi/CPLAuthAPI";
import { getUserProfile, login, logout, verifyImpersonatedUser } from "./AuthCRUD";
import { call, put, takeLatest } from "redux-saga/effects";
import { notifyNativeLogout } from "utils/embeddedApp";

interface ActionWithPayload<T> extends Action {
  payload: T;
}

type SagaHandlers = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorHandler?: (error: any) => void;
  finallyHandler?: () => void;
  successHandler?: () => void;
};

type WithHandlers<T extends Action> = T & {
  handlers?: SagaHandlers;
};

type ActionCreator = (handlers?: SagaHandlers) => WithHandlers<Action>;
type ActionWithPayloadCreator<T> = (payload: T, handlers?: SagaHandlers) => WithHandlers<ActionWithPayload<T>>;
type PersistActionCreator<TPayload> = (payload: TPayload) => ActionWithPayload<IAuthState>;
type ExternalAccessActionCreator = (externalAccess: ExternalAccessData) => ActionWithPayload<IAuthState>;

export type ExternalAccessData = {
  singleUseToken: string;
  externalAccessToken: string;
  accessPath: string;
};

export interface IAuthState {
  user?: ClientPortalUserModel;
  token?: string;
  externalAccess?: ExternalAccessData;
}

type LoginActionData = {
  companyID: string;
  emailAddress: string;
  password: string;
  oneTimePassword?: string;
};

type ImpersonateActionData = {
  impersonatedUserToken: ImpersonationTokenModel;
  verificationCode: string;
};

export const actionTypes = {
  Login: "[Login] Action",
  Logout: "[Logout] Action",
  RequestUser: "[Request User] Action",
  SetUser: "[Set User] Action",
  SetToken: "[Set Token] Action",
  ResetPassword: "[Reset Password] Action",
  SetExternalAccessData: "[Set External Access Data] Action",
  Impersonate: "[Impersonate] Action",
  ResetContentState: "[Reset Content State] Action",
};

const initialAuthState: IAuthState = {
  user: undefined,
  token: undefined,
};

export const reducer = persistReducer(
  { storage: authStorage, key: "cpl-client-portal-auth", whitelist: ["user", "token"] },
  (state: IAuthState = initialAuthState, action: ActionWithPayload<IAuthState>) => {
    switch (action.type) {
      case actionTypes.Logout: {
        return initialAuthState;
      }
      case actionTypes.SetUser: {
        const user = action.payload.user;
        return { ...state, user };
      }
      case actionTypes.SetToken: {
        const token = action.payload.token;
        return { ...state, token };
      }

      case actionTypes.SetExternalAccessData: {
        const externalAccessData = action.payload?.externalAccess;
        return { ...state, externalAccess: externalAccessData };
      }

      default:
        return state;
    }
  }
);

type ActionCreators = {
  login: ActionWithPayloadCreator<LoginActionData>;
  logout: ActionCreator;
  requestUser: ActionCreator;
  setUser: PersistActionCreator<ClientPortalUserModel>;
  setToken: PersistActionCreator<string>;
  resetPassword: ActionWithPayloadCreator<string>;
  setExternalAccessTokens: ExternalAccessActionCreator;
  impersonate: ActionWithPayloadCreator<ImpersonateActionData>;
  resetContentState: ActionCreator;
};

export const actions: ActionCreators = {
  login: (payload, handlers) => ({ type: actionTypes.Login, payload, handlers }),
  logout: (handlers) => ({ type: actionTypes.Logout, handlers }),
  requestUser: (handlers) => ({ type: actionTypes.RequestUser, handlers }),
  setUser: (user: ClientPortalUserModel) => ({ type: actionTypes.SetUser, payload: { user } }),
  setToken: (token: string) => ({ type: actionTypes.SetToken, payload: { token } }),
  resetPassword: (payload, handlers) => ({ type: actionTypes.ResetPassword, payload, handlers }),
  setExternalAccessTokens: (externalAccess: ExternalAccessData) => ({
    type: actionTypes.SetExternalAccessData,
    payload: { externalAccess },
  }),
  impersonate: (payload, handlers) => ({ type: actionTypes.Impersonate, payload, handlers }),
  resetContentState: (handlers) => ({ type: actionTypes.ResetContentState, handlers }),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sagaHandler = <T extends Action>(saga: any) =>
  function* (action: WithHandlers<T>) {
    try {
      yield call(saga, action);
      if (action.handlers?.successHandler !== undefined) {
        yield call(action.handlers.successHandler);
      }
    } catch (error) {
      if (action.handlers?.errorHandler !== undefined) {
        yield call(action.handlers.errorHandler, error);
      }
    } finally {
      if (action.handlers?.finallyHandler !== undefined) {
        yield call(action.handlers.finallyHandler);
      }
    }
  };

export function* saga() {
  yield takeLatest(
    actionTypes.Login,
    sagaHandler(function* loginSaga(action: ActionWithPayload<LoginActionData>) {
      const token = yield login(
        action.payload.companyID,
        action.payload.emailAddress,
        action.payload.password,
        action.payload.oneTimePassword
      );
      yield put(actions.setToken(token));
      yield put(actions.requestUser());
    })
  );

  yield takeLatest(
    actionTypes.RequestUser,
    sagaHandler(function* requestUserSaga() {
      const user = yield getUserProfile();
      yield put(actions.setUser(user));
    })
  );

  yield takeLatest(
    actionTypes.Logout,
    sagaHandler(function* logoutSaga() {
      yield logout();
      notifyNativeLogout();
    })
  );

  yield takeLatest(
    actionTypes.ResetPassword,
    sagaHandler(function* resetPasswordSaga(action: ActionWithPayload<string>) {
      yield put(actions.resetPassword(action.payload));
    })
  );

  yield takeLatest(
    actionTypes.Impersonate,
    sagaHandler(function* impersonateSaga(action: ActionWithPayload<ImpersonateActionData>) {
      const token = yield verifyImpersonatedUser(action.payload.verificationCode, action.payload.impersonatedUserToken);
      yield put(actions.setToken(token));
      yield put(actions.requestUser());
    })
  );
}
